/**
 * Dashboard Slice
 *
 * Redux state management for the Admin Dashboard.
 * Handles KPI cards, charts, gateway services, and list data.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// ============================================================================
// Display-Only Type Unions (used by components)
// ============================================================================

/** Badge variant for KPI cards */
export type KpiBadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral";

/** Accent color options */
export type AccentColor =
  | "indigo"
  | "emerald"
  | "violet"
  | "rose"
  | "amber"
  | "blue"
  | "cyan"
  | "teal"
  | "red"
  | "orange"
  | "pink"
  | "slate";

/** Icon key for mapping to Lucide icons in components */
export type KpiIconKey =
  | "home"
  | "check-circle"
  | "clock"
  | "monitor"
  | "package"
  | "users"
  | "trending-up"
  | "dollar-sign"
  | "activity";

/** Status variant for list items */
export type StatusVariant =
  | "urgent"
  | "pending"
  | "new"
  | "success"
  | "warning"
  | "danger"
  | "info";

/** Avatar badge variant */
export type AvatarBadgeVariant =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral";

/** Service status */
export type ServiceStatus = "online" | "offline";

/** Time range options for chart */
export type TimeRange = "12M" | "6M" | "3M";

// ============================================================================
// Raw API Types (snake_case matching Django backend)
// ============================================================================

/** KPI badge configuration */
export interface RawKpiBadge {
  text: string;
  variant: string;
}

/** KPI Card data from API */
export interface RawKpiCard {
  id: string;
  label: string;
  value: string;
  unit?: string;
  badge?: RawKpiBadge;
}

/** Chart data point */
export interface RawTenantGrowthDataPoint {
  month: string;
  total_tenants: number;
  active_tenants: number;
}

/** Gateway service data */
export interface RawGatewayService {
  id: string;
  name: string;
  status: string;
  latency: string | null;
}

/** Pending application item */
export interface RawPendingApplication {
  id: string;
  title: string;
  subtitle?: string;
  status: string;
  status_label: string;
  submitted_at?: string;
}

/** Recently onboarded tenant item */
export interface RawRecentlyOnboarded {
  id: string;
  title: string;
  subtitle?: string;
  initials: string;
  status?: string;
  active_since?: string | null;
  domain?: string | null;
}

/** Product request item */
export interface RawProductRequest {
  id: string;
  title: string;
  subtitle?: string;
  status: string;
  status_label: string;
}

// ============================================================================
// Dashboard State
// ============================================================================

export interface DashboardState {
  // KPI Cards
  kpiCards: RawKpiCard[];
  kpiCardsLoading: boolean;
  kpiCardsError: string | null;

  // Tenant Growth Chart
  tenantGrowthData: Record<string, RawTenantGrowthDataPoint[]>;
  tenantGrowthLoading: boolean;
  tenantGrowthError: string | null;
  selectedTimeRange: TimeRange;

  // Gateway Services
  gatewayServices: RawGatewayService[];
  gatewayServicesLoading: boolean;
  gatewayServicesError: string | null;

  // Pending Applications
  pendingApplications: RawPendingApplication[];
  pendingApplicationsLoading: boolean;
  pendingApplicationsError: string | null;

  // Recently Onboarded
  recentlyOnboarded: RawRecentlyOnboarded[];
  recentlyOnboardedLoading: boolean;
  recentlyOnboardedError: string | null;

  // Product Requests
  productRequests: RawProductRequest[];
  productRequestsLoading: boolean;
  productRequestsError: string | null;

  // Overall dashboard state
  lastRefreshed: string | null;
  isRefreshing: boolean;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: DashboardState = {
  // KPI Cards
  kpiCards: [],
  kpiCardsLoading: false,
  kpiCardsError: null,

  // Tenant Growth Chart
  tenantGrowthData: {},
  tenantGrowthLoading: false,
  tenantGrowthError: null,
  selectedTimeRange: "3M",

  // Gateway Services
  gatewayServices: [],
  gatewayServicesLoading: false,
  gatewayServicesError: null,

  // Pending Applications
  pendingApplications: [],
  pendingApplicationsLoading: false,
  pendingApplicationsError: null,

  // Recently Onboarded
  recentlyOnboarded: [],
  recentlyOnboardedLoading: false,
  recentlyOnboardedError: null,

  // Product Requests
  productRequests: [],
  productRequestsLoading: false,
  productRequestsError: null,

  // Overall dashboard state
  lastRefreshed: null,
  isRefreshing: false,
};

// ============================================================================
// Slice
// ============================================================================

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // -------------------------------------------------------------------------
    // KPI Cards
    // -------------------------------------------------------------------------
    setKpiCards: (state, action: PayloadAction<RawKpiCard[]>) => {
      state.kpiCards = action.payload;
    },
    setKpiCardsLoading: (state, action: PayloadAction<boolean>) => {
      state.kpiCardsLoading = action.payload;
    },
    setKpiCardsError: (state, action: PayloadAction<string | null>) => {
      state.kpiCardsError = action.payload;
    },

    // -------------------------------------------------------------------------
    // Tenant Growth Chart
    // -------------------------------------------------------------------------
    setTenantGrowthData: (
      state,
      action: PayloadAction<Record<string, RawTenantGrowthDataPoint[]>>,
    ) => {
      state.tenantGrowthData = action.payload;
    },
    setTenantGrowthDataForRange: (
      state,
      action: PayloadAction<{
        range: string;
        data: RawTenantGrowthDataPoint[];
      }>,
    ) => {
      state.tenantGrowthData[action.payload.range] = action.payload.data;
    },
    setTenantGrowthLoading: (state, action: PayloadAction<boolean>) => {
      state.tenantGrowthLoading = action.payload;
    },
    setTenantGrowthError: (state, action: PayloadAction<string | null>) => {
      state.tenantGrowthError = action.payload;
    },
    setSelectedTimeRange: (state, action: PayloadAction<TimeRange>) => {
      state.selectedTimeRange = action.payload;
    },

    // -------------------------------------------------------------------------
    // Gateway Services
    // -------------------------------------------------------------------------
    setGatewayServices: (state, action: PayloadAction<RawGatewayService[]>) => {
      state.gatewayServices = action.payload;
    },
    setGatewayServicesLoading: (state, action: PayloadAction<boolean>) => {
      state.gatewayServicesLoading = action.payload;
    },
    setGatewayServicesError: (state, action: PayloadAction<string | null>) => {
      state.gatewayServicesError = action.payload;
    },
    updateGatewayServiceStatus: (
      state,
      action: PayloadAction<{
        id: string;
        status: string;
        latency?: string | null;
      }>,
    ) => {
      const service = state.gatewayServices.find(
        (s) => s.id === action.payload.id,
      );
      if (service) {
        service.status = action.payload.status;
        service.latency = action.payload.latency ?? null;
      }
    },

    // -------------------------------------------------------------------------
    // Pending Applications
    // -------------------------------------------------------------------------
    setPendingApplications: (
      state,
      action: PayloadAction<RawPendingApplication[]>,
    ) => {
      state.pendingApplications = action.payload;
    },
    setPendingApplicationsLoading: (state, action: PayloadAction<boolean>) => {
      state.pendingApplicationsLoading = action.payload;
    },
    setPendingApplicationsError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.pendingApplicationsError = action.payload;
    },
    removePendingApplication: (state, action: PayloadAction<string>) => {
      state.pendingApplications = state.pendingApplications.filter(
        (app) => app.id !== action.payload,
      );
    },

    // -------------------------------------------------------------------------
    // Recently Onboarded
    // -------------------------------------------------------------------------
    setRecentlyOnboarded: (
      state,
      action: PayloadAction<RawRecentlyOnboarded[]>,
    ) => {
      state.recentlyOnboarded = action.payload;
    },
    setRecentlyOnboardedLoading: (state, action: PayloadAction<boolean>) => {
      state.recentlyOnboardedLoading = action.payload;
    },
    setRecentlyOnboardedError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.recentlyOnboardedError = action.payload;
    },

    // -------------------------------------------------------------------------
    // Product Requests
    // -------------------------------------------------------------------------
    setProductRequests: (state, action: PayloadAction<RawProductRequest[]>) => {
      state.productRequests = action.payload;
    },
    setProductRequestsLoading: (state, action: PayloadAction<boolean>) => {
      state.productRequestsLoading = action.payload;
    },
    setProductRequestsError: (state, action: PayloadAction<string | null>) => {
      state.productRequestsError = action.payload;
    },
    updateProductRequestStatus: (
      state,
      action: PayloadAction<{
        id: string;
        status: string;
        status_label: string;
      }>,
    ) => {
      const request = state.productRequests.find(
        (r) => r.id === action.payload.id,
      );
      if (request) {
        request.status = action.payload.status;
        request.status_label = action.payload.status_label;
      }
    },
    removeProductRequest: (state, action: PayloadAction<string>) => {
      state.productRequests = state.productRequests.filter(
        (r) => r.id !== action.payload,
      );
    },

    // -------------------------------------------------------------------------
    // Overall Dashboard
    // -------------------------------------------------------------------------
    setLastRefreshed: (state, action: PayloadAction<string | null>) => {
      state.lastRefreshed = action.payload;
    },
    setIsRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload;
    },
    clearDashboard: (state) => {
      Object.assign(state, initialState);
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  // KPI Cards
  setKpiCards,
  setKpiCardsLoading,
  setKpiCardsError,

  // Tenant Growth Chart
  setTenantGrowthData,
  setTenantGrowthDataForRange,
  setTenantGrowthLoading,
  setTenantGrowthError,
  setSelectedTimeRange,

  // Gateway Services
  setGatewayServices,
  setGatewayServicesLoading,
  setGatewayServicesError,
  updateGatewayServiceStatus,

  // Pending Applications
  setPendingApplications,
  setPendingApplicationsLoading,
  setPendingApplicationsError,
  removePendingApplication,

  // Recently Onboarded
  setRecentlyOnboarded,
  setRecentlyOnboardedLoading,
  setRecentlyOnboardedError,

  // Product Requests
  setProductRequests,
  setProductRequestsLoading,
  setProductRequestsError,
  updateProductRequestStatus,
  removeProductRequest,

  // Overall Dashboard
  setLastRefreshed,
  setIsRefreshing,
  clearDashboard,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
